import {
  AudiosQueryObject,
  EpisodeGarmSummary,
  ISegment,
  ITranscript,
  ParagraphData,
} from '@insights_module/types/Episode';
import { CategoryType, GarmRiskLevelType } from '@insights_module/types/BrandSafety';
import { ContextualForShowItem } from '@insights_module/types/ContextualAndEntity';
import {
  AQLBrandSafetyGarmFilterQuery,
  AQLBrandSafetyGarmQuery,
  AQLContextualFilterQuery,
  AQLContextualQuery,
  AQLGroupsQuery,
  AQLIdQuery,
  AQLLinkedNamedEntitiesFilterQuery,
  AQLLinkedNamedEntitiesQuery,
  AQLNamedEntitiesFilterQuery,
  AQLNamedEntitiesQuery,
  AQLTitleQuery,
} from '@insights_module/adc_ql/queries';
import { AQLQuery, AQLQueryValue } from '@insights_module/adc_ql/types';
import { convertSecondsToHMS } from '@utils/dateUtils';

type ParamsType = { [s: string]: unknown } | ArrayLike<unknown> | ContextualForShowItem;

export function getEpisodesQueryObject(
  groupId: string | null,
  params: ParamsType,
  includeUnprocessed = false
): AudiosQueryObject {
  const query = {
    all: [] as AQLQueryValue[],
  };

  if (groupId) {
    query.all.push(new AQLGroupsQuery('includes', new AQLIdQuery('eq', groupId) as AQLQuery));
  }

  // temporally we check if all filters are removed, and if they are we add one that give us all processed episodes but only if we don't want to include unprocessed
  if (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ((params.all && params.all.length === 0) || (params.any && params.any.length === 0)) &&
    !includeUnprocessed
  ) {
    query.all.push(
      new AQLBrandSafetyGarmQuery([
        new AQLBrandSafetyGarmFilterQuery('adult_explicit_content', 'range', {
          gte: 'unrelated',
          lte: 'floor',
        }),
      ])
    );
  }

  // in case we have a group id, that means all episodes are part of a group and in that case we don't want to use global query on those episodes
  // but we need to add a filter that will give us all processed episodes if it's not already added and only if we don't want to include unprocessed
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (!params.brand_safety_garm && groupId && !includeUnprocessed) {
    query.all.push(
      new AQLBrandSafetyGarmQuery([
        new AQLBrandSafetyGarmFilterQuery('adult_explicit_content', 'range', {
          gte: 'unrelated',
          lte: 'floor',
        }),
      ])
    );
  }

  Object.entries(params).forEach(([key, value]) => {
    if (key === null || value === null) {
      return;
    }

    // if we have a group id, that means all episodes are part of a group and in that case we don't want to use global query on those episodes
    // we want to have all episodes from the show before show details filters are applied
    if ((key === 'all' || key === 'any') && Array.isArray(value) && !groupId) {
      query.all.push({ [key]: value });
      return;
    }

    if (Array.isArray(value) && !value.length) {
      return;
    }

    if (typeof value === 'string' && !value.length) {
      return;
    }

    if (key === 'brand_safety_garm') {
      query.all.push({ [key]: value } as AQLQueryValue);
      return;
    }

    if (key === 'brand_safety_iab') {
      query.all.push({ [key]: value } as AQLQueryValue);
      return;
    }

    if (key === 'named_entities' && value) {
      query.all.push(
        new AQLNamedEntitiesQuery('includes', [
          new AQLNamedEntitiesFilterQuery('value', 'eq', value),
        ])
      );
    }

    if (key === 'linked_named_entities' && value) {
      query.all.push(
        new AQLLinkedNamedEntitiesQuery('includes', [
          new AQLLinkedNamedEntitiesFilterQuery('id', 'eq', value),
        ])
      );
    }

    if (key === 'contextual') {
      query.all.push(
        new AQLContextualQuery('includes', [
          new AQLContextualFilterQuery('name', 'eq', value?.display_name || value.name),
          new AQLContextualFilterQuery(
            'taxonomy',
            'eq',
            value.taxonomy?.[0].taxonomy || value.taxonomy
          ),
        ])
      );
    }

    if (key === 'global_search' && typeof value === 'string' && value.length) {
      query.all.push(new AQLTitleQuery('includes', value as string));
    }

    if (key === 'published_date') {
      query.all.push({
        [key]: {
          range: value,
        },
      } as never);
    }
  });

  return query;
}

export const aggregateGarmForEpisode = (episodeSegments: ISegment[]) => {
  const aggregatePerSegment = (segment: ISegment) => {
    return Object.entries(segment.garm).reduce((acc, [category, level]) => {
      const typedCategory = category as CategoryType;
      // we get uppercase levels from the backend
      const lowerCaseLevel = level.toLowerCase() as GarmRiskLevelType;

      if (!acc[typedCategory]) {
        acc[typedCategory] = {
          unrelated: 0,
          low: 0,
          medium: 0,
          high: 0,
          floor: 0,
          [lowerCaseLevel]: 1,
        };
        return acc;
      }
      acc[typedCategory][lowerCaseLevel] += 1;

      return acc;
    }, {} as EpisodeGarmSummary);
  };

  return episodeSegments.reduce((acc, segment) => {
    const segmentSummary = aggregatePerSegment(segment);

    return Object.entries(segmentSummary).reduce((acc, [category, levelSummary]) => {
      const typedCategory = category as CategoryType;

      Object.entries(levelSummary).forEach(([level, count]) => {
        const typedLevel = level as GarmRiskLevelType;

        if (!acc[typedCategory]) {
          acc[typedCategory] = { unrelated: 0, low: 0, medium: 0, high: 0, floor: 0 };
        }

        acc[typedCategory][typedLevel] += count;
      });

      return acc;
    }, acc);
  }, {} as EpisodeGarmSummary);
};

type SegmentId = { id: number; start: number };

type SegmentIdsByGarm = {
  [key in CategoryType]: {
    [key in GarmRiskLevelType]: SegmentId[];
  };
};

export const aggregateSegmentIdsByGarm = (segments: ISegment[]) => {
  return segments.reduce((acc, segment) => {
    Object.entries(segment.garm).forEach(([category, level]) => {
      const typedCategory = category as CategoryType;
      const typedLevel = level.toLowerCase() as GarmRiskLevelType;

      if (!acc[typedCategory] && typedLevel !== 'unrelated') {
        acc[typedCategory] = {
          unrelated: [],
          low: [],
          medium: [],
          high: [],
          floor: [],
          [typedLevel]: [{ id: segment.id, start: segment.startTime }],
        };
        return;
      }

      if (typedLevel !== 'unrelated') {
        acc[typedCategory][typedLevel].push({ id: segment.id, start: segment.startTime });
      }
    });

    return acc;
  }, {} as SegmentIdsByGarm);
};

type SegmentsByIabCategory = {
  [key: string]: number[];
};

export const aggregateSegmentIdsByContextualCategory = (
  categories: string[],
  segments: ISegment[]
) => {
  return segments.reduce((acc, segment) => {
    const segmentCategories = segment.contextual.categories?.map((category) => category.name) || [];
    categories.forEach((category) => {
      if (!acc[category]) {
        acc[category] = [];
      }

      if (segmentCategories.includes(category)) {
        acc[category].push(segment.id);
      }
    });
    return acc;
  }, {} as SegmentsByIabCategory);
};

export function scrollToSegment(segmentId: number) {
  const element = document.getElementById(`segment-${segmentId}`);

  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
}

export function scrollToParagraph(num: number) {
  const element = document.querySelector(`p[data-time='${Number(num)}']`);

  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
}

export function getHighestRisk(arr: GarmRiskLevelType[]): GarmRiskLevelType {
  const values = ['unrelated', 'low', 'medium', 'high', 'floor'] as GarmRiskLevelType[];

  return arr.reduce((highestValue: GarmRiskLevelType, currentValue: GarmRiskLevelType) => {
    const highestIndex = values.indexOf(highestValue);
    const currentIndex = values.indexOf(currentValue.toLowerCase() as GarmRiskLevelType);

    if (currentIndex > highestIndex) {
      return currentValue.toLowerCase() as GarmRiskLevelType;
    }

    return highestValue;
  }, 'unrelated' as GarmRiskLevelType);
}

/**
 * Compares two risk levels and returns a value indicating their sort order.
 * @param {GarmRiskLevelType} a The first risk level to compare.
 * @param {GarmRiskLevelType} b The second risk level to compare.
 * @param {'asc' | 'desc'} [order='asc'] The order in which to sort the risk levels.
 * @returns {number} A value indicating the sort order of the two risk levels.
 */
export function sortByRiskLevels(
  a: GarmRiskLevelType,
  b: GarmRiskLevelType,
  order: 'asc' | 'desc' = 'asc'
): number {
  const values = ['unrelated', 'low', 'medium', 'high', 'floor'];

  const indexA = values.indexOf(a.toLowerCase());
  const indexB = values.indexOf(b.toLowerCase());

  const comparison = order === 'desc' ? indexB - indexA : indexA - indexB;

  return comparison === 0 ? 0 : comparison / Math.abs(comparison);
}

/**
 * Highlights all matching substrings in the given elements with a wrapper element.
 * Adds 'data-index' and 'data-name' attributes to each wrapper element for indexing.
 * @param {Element[]} elements The elements to search within.
 * @param {string} substring The substring to search for.
 * @param {string[]} classNames A set of class names to add to the wrapper elements.
 * @param {string} dataName The value to use for the 'data-name' attribute of the wrapper elements.
 * @param {boolean} caseSensitive Whether to perform a case-sensitive search or not.
 */
export function highlightMatchingSubstrings(
  elements: Element[],
  substring: string,
  classNames: string[],
  dataName: string,
  caseSensitive = true
): void {
  const searchValue = caseSensitive ? substring : substring.toLowerCase();

  for (const [index, element] of elements.entries()) {
    let node = element.firstChild;

    while (node) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        const textValue = caseSensitive ? text : text.toLowerCase();
        const matchIndex = textValue.indexOf(searchValue);
        if (matchIndex >= 0) {
          const before = text.substring(0, matchIndex);
          const match = text.substring(matchIndex, matchIndex + substring.length);
          const after = text.substring(matchIndex + substring.length);

          const beforeNode = document.createTextNode(before);
          const matchNode = document.createElement('span');
          matchNode.setAttribute('data-index', index.toString());
          matchNode.setAttribute('data-name', dataName);
          matchNode.classList.add(...classNames);

          matchNode.appendChild(document.createTextNode(match));

          const afterNode = document.createTextNode(after);

          element.replaceChild(afterNode, node);
          element.insertBefore(matchNode, afterNode);
          element.insertBefore(beforeNode, matchNode);
          node = afterNode;
        }
      }
      node = node.nextSibling;
    }
  }
}

/**
 * Removes highlighting of all matching substrings in the given context element.
 * @param {Element} contextElement The root element to search within.
 * @param {string} dataName The value of the 'data-name' attribute used to find the matching spans.
 */
export function removeMatchingSubstringHighlighting(
  contextElement: Element,
  dataName: string
): void {
  const matchingSpans = contextElement.querySelectorAll(`span[data-name='${dataName}']`);
  for (const span of matchingSpans) {
    const text = span.textContent || '';
    const textNode = document.createTextNode(text);
    const parent = span.parentNode;

    if (parent) {
      parent.replaceChild(textNode, span);
      concatenateTextNodes(parent);
    }
  }
}

/**
 * Concatenates all adjacent text nodes into a single text node.
 * @param {Node} parent The parent element containing the text nodes.
 */
function concatenateTextNodes(parent: Node): void {
  let currentTextNode: Text | null = null;
  for (const childNode of Array.from(parent.childNodes)) {
    if (childNode.nodeType !== Node.TEXT_NODE) continue;

    const textNode = childNode as Text;

    if (!currentTextNode) {
      currentTextNode = textNode;
    } else {
      currentTextNode.textContent += textNode.textContent || '';
    }

    parent.removeChild(textNode);
  }

  if (currentTextNode) parent.appendChild(currentTextNode);
}

/**
 * Parse the transcript into array of paragraphs data, containing the words, start time, end time, and paragraph string.
 * @param {ITranscript[]} transcript The transcript to parse.
 * @returns {ParagraphData[]} An array of paragraphs data.
 */
export function getParagraphsDataForSegment(transcript: ITranscript[]): ParagraphData[] {
  const emptyParagraph: ParagraphData = {
    paragraphWords: [],
    startTime: '',
    endTime: '',
    paragraphString: '',
    speaker: null,
  };

  const END_OF_PARAGRAPH = ['.', '!', '?', ';'];
  let latestSpeaker: number | null | undefined = null;

  return transcript.reduce(
    (paragraphs, word, index) => {
      const { alternatives, start_time, type, metadata } = word;
      const lastParagraph = paragraphs[paragraphs.length - 1];

      if (metadata) {
        if (latestSpeaker !== metadata?.speaker_id) {
          lastParagraph.speaker = metadata?.speaker_id;
          latestSpeaker = metadata?.speaker_id;
        }
      }

      if (type === 'punctuation' && END_OF_PARAGRAPH.includes(alternatives[0].content)) {
        lastParagraph.paragraphString += alternatives[0].content;
        lastParagraph.endTime =
          lastParagraph.paragraphWords[lastParagraph.paragraphWords.length - 1]?.end_time;

        // in some cases when previous paragraph end time is the same as current paragraph start time,
        // and end of paragraph punctuation between them,
        // we end up with empty paragraph that has only copy of that punctuation, so we remove that paragraph
        if (lastParagraph.paragraphWords.length === 0) {
          paragraphs.pop();
        }

        if (index !== transcript.length - 1) {
          paragraphs.push({ ...emptyParagraph, paragraphWords: [] });
        }

        return paragraphs;
      }

      if (lastParagraph.paragraphWords.length === 0) {
        lastParagraph.startTime = start_time;
        lastParagraph.paragraphString += alternatives[0].content;
        lastParagraph.paragraphWords.push(word);

        return paragraphs;
      }

      if (type === 'punctuation') {
        lastParagraph.paragraphString += alternatives[0].content;

        return paragraphs;
      }

      if (type === 'pronunciation' && index === transcript.length - 1) {
        lastParagraph.endTime = word.end_time;
      }

      // upside down question mark is punctuation, but it goes in front of the word so we don't need space between the word and the punctuation
      if (lastParagraph.paragraphString.endsWith('¿')) {
        lastParagraph.paragraphString += `${alternatives[0].content}`;
        lastParagraph.paragraphWords.push(word);

        return paragraphs;
      }

      lastParagraph.paragraphString += ` ${alternatives[0].content}`;
      lastParagraph.paragraphWords.push(word);

      return paragraphs;
    },
    [{ ...emptyParagraph, paragraphWords: [] }] as ParagraphData[]
  );
}

/**
 * Searches for a `span` element inside an array of `HTMLElement` objects with a matching `data-index` attribute value.
 * @param {HTMLElement[]} elements - The array of `HTMLElement` objects to search through.
 * @param {number} index - The value of the `data-index` attribute to match against.
 * @returns {HTMLElement | null} - The matching `span` element or `null` if no match is found.
 */
export const getSpanElementBySpanIndex = (
  elements: HTMLElement[],
  index: number
): HTMLElement | null => {
  let matchingSpan = null;
  elements.some((element) => {
    const spans = Array.from(element.querySelectorAll('span'));
    return spans.find((span) => {
      if (span?.getAttribute('data-index') === String(index)) {
        matchingSpan = span;
        return true;
      }

      return false;
    });
  });

  return matchingSpan as HTMLElement | null;
};

/**
 * Finds all `HTMLElement` objects containing the specified text content within the provided `contextElement`.
 * @param {HTMLElement} contextElement - The `HTMLElement` object to search within.
 * @param {string} text - The text content to search for.
 * @param {boolean} [caseSensitive]  - Whether or not the search should be case sensitive.
 * @returns {HTMLElement[]} - An array of all matching `HTMLElement` objects.
 */
export function getElementsByContainingText(
  contextElement: HTMLElement,
  text: string,
  caseSensitive = true
): HTMLElement[] {
  const regex = new RegExp(text, 'g' + (caseSensitive ? '' : 'i'));
  const pTags = contextElement.querySelectorAll('p');

  return Array.from(pTags).filter((p) => p.textContent?.match(regex));
}

function removeSubstrings(str: string, subStr: string): string {
  const regex = new RegExp(`${subStr.trim()}`, 'gi');
  return str.replaceAll(regex, '');
}

/**
 * Remove all Show titles from episode title
 * @param {string} episodeTitle - The episode title
 * @param {string} showTitle - The show title
 * @returns {string} - The episode title without show title
 */

export function removeShowTitleFromEpisodeTitle(episodeTitle: string, showTitle: string): string {
  try {
    let _episodeTitle = removeSubstrings(episodeTitle, showTitle).trim();
    while (_episodeTitle.startsWith('-')) {
      _episodeTitle = _episodeTitle.slice(1).trim();
    }
    return _episodeTitle;
  } catch (error) {
    return episodeTitle;
  }
}

/**
 * Returns audio time time to fixed num without decimals
 * @param {number} val - startTime
 * @returns {number} - Integer
 */

export function audioTimeToFixedNum(val: number): number {
  return Number(val.toFixed());
}

/**
 * @param {ISegment[]} arr - array of segmentsWithParagraphs
 * @param {number} val - Integer
 * @returns {ISegment | undefined}
 */

export const findSegmentByStartTime = (arr: ISegment[], val: number): ISegment | undefined => {
  return arr?.find((item) => {
    return audioTimeToFixedNum(Number(item.startTime)) === audioTimeToFixedNum(val);
  });
};

/**
 * @param {ISegment[]} arr - array of segmentsWithParagraphs
 * @param {number} val - time
 * @returns {ISegment | undefined}
 */

export const findNearestTimeSegment = (arr: ISegment[], val: number): ISegment | null => {
  if (!arr || !arr.length) return null;

  return arr.reduce((acc, obj) => (val - acc?.startTime < obj?.startTime - val ? acc : obj));
};

/**
 * @param {ParagraphData[]} arr - array of segment paragraph data
 * @param {number} val - time
 * @returns {ParagraphData | undefined}
 */

export const findParagraphByStartTime = (
  arr: ParagraphData[] | undefined,
  val: number
): ParagraphData | undefined => {
  return arr?.find((item) => {
    return audioTimeToFixedNum(Number(item.startTime)) === audioTimeToFixedNum(val);
  });
};

/**
 * @param {ParagraphData[]} arr - array of segment paragraph data
 * @param {number} val - time
 * @returns {ParagraphData | undefined}
 */

export const findNearestTimeParagraph = (
  arr: ParagraphData[] | undefined,
  val: number
): ParagraphData | undefined => {
  return arr?.reduce(
    (acc, obj) => (val - Number(acc?.startTime) < Number(obj?.startTime) - val ? acc : obj),
    arr ? arr[0] : undefined
  );
};

/**
 * @param {ITranscript[]} items - array of segment paragraph data
 * @returns {string}
 */
export const formatDiarizedTranscriptTXTFile = (items: ITranscript[]): string => {
  let speaker: number | null | undefined = null;
  let textResult = '';
  let start = '';
  let end = '';
  let str = '';

  const paragraphs = getParagraphsDataForSegment(items);

  paragraphs.forEach((item, index) => {
    const paragraph = str.length ? ` ${item.paragraphString}` : item.paragraphString;
    str += paragraph;

    if (typeof item.speaker === 'number' && item.speaker !== speaker) {
      speaker = item.speaker;
      start = item.startTime;
    }

    if (paragraphs[index + 1] && paragraphs[index + 1].speaker !== item.speaker) {
      end = item.endTime;

      textResult += `\n\nspeaker${speaker} ${convertSecondsToHMS(
        Number(start)
      )} - ${convertSecondsToHMS(Number(end))}\n${str}`;

      str = '';
    }
  });

  return textResult;
};
